import type { PortalAnswerValue } from "./portal-item-answers.js"
import type { PortalRequestItemKind } from "./portal-requests.js"

/* Questions that appear from earlier answers. An item can name an earlier
   yes-or-no or choice item in its request and the answer that shows it.
   The portal, the server's counts and the reminders all read visibility
   from this one file, so a hidden item is never asked for, never missing
   and never overdue. */

/** The answer that shows an item: `true` or `false` on a yes-or-no item,
 * one of the choices on a choice item. */
export type PortalShowWhenAnswer = string | boolean

/** A stored condition. It names the item it reads by id. */
export type PortalItemShowWhen = Readonly<{
  itemId: string
  equals: PortalShowWhenAnswer
}>

/** A condition as a builder sends it. The items have no ids yet, so it names
 * the earlier item by its name, which is unique in a request. */
export type PortalItemShowWhenInput = Readonly<{
  itemName: string
  equals: PortalShowWhenAnswer
}>

/** The kinds whose answer can show another item. */
export const portalShowWhenSourceKinds = [
  "yes_no",
  "choice",
] as const satisfies readonly PortalRequestItemKind[]

const isSourceKind = (kind: PortalRequestItemKind | undefined): boolean =>
  portalShowWhenSourceKinds.some((source) => source === kind)

/** What visibility reads from an item: a row carries `id`, an API
 * response `itemId`. */
type VisibilityItem = Readonly<{
  kind?: PortalRequestItemKind
  showWhen?: PortalItemShowWhen | null
  answer?: Readonly<{ value: PortalAnswerValue }> | null
}> &
  (Readonly<{ id: string }> | Readonly<{ itemId: string }>)

const idOf = (item: VisibilityItem): string =>
  "itemId" in item ? item.itemId : item.id

/** Whether an answer is the one a condition waits for. A choice with
 * several answers matches when the answer is one of them. */
export const portalShowWhenMatches = (
  value: PortalAnswerValue | undefined,
  equals: PortalShowWhenAnswer
): boolean =>
  value === equals ||
  (Array.isArray(value) && value.some((entry: unknown) => entry === equals))

/**
 * Whether the item is hidden: its condition names an earlier question
 * whose answer is not the one it waits for, or that is not answered yet.
 * A condition whose question was removed, or is no longer a yes-or-no or
 * choice item, shows the item: asking one more question is safer than
 * losing one. The queue SQL (itemShownSql) reads the same rule.
 */
export const isPortalItemHidden = (
  item: VisibilityItem,
  items: readonly VisibilityItem[]
): boolean => {
  const condition = item.showWhen
  if (!condition) return false
  const source = items.find((entry) => idOf(entry) === condition.itemId)
  if (!source || !isSourceKind(source.kind)) return false
  return !portalShowWhenMatches(source.answer?.value, condition.equals)
}

/** The items the client is asked for, in their order. */
export const shownPortalItems = <T extends VisibilityItem>(
  items: readonly T[]
): T[] => items.filter((item) => !isPortalItemHidden(item, items))

/** The items with a hidden one no longer required. An item the client's
 * answer skipped is not asked, so it is never missing or overdue and never
 * holds up a submit. Its own answer, if it has one, is kept but ignored. */
export const requiredWhenShown = <
  T extends VisibilityItem & { required: boolean },
>(
  items: readonly T[]
): T[] =>
  items.map((item) =>
    item.required && isPortalItemHidden(item, items)
      ? { ...item, required: false }
      : item
  )

/** An item in a builder's list, for checking its condition. */
type BuilderItem = Readonly<{
  name: string
  kind: PortalRequestItemKind
  options: readonly string[]
  showWhen?: PortalItemShowWhenInput | null
}>

/**
 * Why the condition on the item at `index` of a builder's list cannot
 * work, or null. A condition names an earlier yes-or-no or choice item
 * that is always asked, and one of that item's answers.
 */
export const portalShowWhenItemProblem = (
  items: readonly BuilderItem[],
  index: number
): string | null => {
  const condition = items[index]?.showWhen
  if (!condition) return null
  const sourceIndex = items.findIndex(
    (entry) => entry.name === condition.itemName
  )
  const source = items[sourceIndex]
  if (!source || sourceIndex >= index)
    return "Choose a question above this item."
  if (!isSourceKind(source.kind))
    return "Choose a yes or no question or a choice."
  if (source.showWhen) return "Choose a question that is always asked."
  if (source.kind === "yes_no")
    return typeof condition.equals === "boolean" ? null : "Choose Yes or No."
  return typeof condition.equals === "string" &&
    source.options.includes(condition.equals)
    ? null
    : "Choose one of that question's choices."
}

/** The first condition in a builder's list that cannot work, or null. */
export const portalShowWhenProblem = (
  items: readonly BuilderItem[]
): { index: number; message: string } | null => {
  for (const index of items.keys()) {
    const message = portalShowWhenItemProblem(items, index)
    if (message) return { index, message }
  }
  return null
}

/**
 * New ids for a list of new items, and each condition pointed at the new id
 * of the item it names. `refOf` is how a condition names its item: a name
 * in a builder's list, or a template item's id when a template is copied.
 * A condition whose item is not in the list is dropped.
 */
export const resolvePortalShowWhen = <T>(
  items: readonly T[],
  input: {
    ids: readonly string[]
    refOf(item: T): string
    conditionOf(
      item: T
    ): Readonly<{ ref: string; equals: PortalShowWhenAnswer }> | null
  }
): (PortalItemShowWhen | null)[] => {
  const idByRef = new Map(
    items.map((item, index) => [input.refOf(item), input.ids[index]!])
  )
  return items.map((item) => {
    const condition = input.conditionOf(item)
    const itemId = condition ? idByRef.get(condition.ref) : undefined
    return condition && itemId ? { itemId, equals: condition.equals } : null
  })
}

/** A stored condition as a builder holds it: by the name of the item it
 * reads. Null when that item is not in the list. */
export const portalShowWhenInput = (
  showWhen: PortalItemShowWhen | null | undefined,
  items: readonly Readonly<{ itemId: string; name: string }>[]
): PortalItemShowWhenInput | null => {
  if (!showWhen) return null
  const source = items.find((item) => item.itemId === showWhen.itemId)
  return source ? { itemName: source.name, equals: showWhen.equals } : null
}
